import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PwaNativeBridgeService } from '../pwa/services/pwa-native-bridge.service';
import { FileItem } from '../shared/models/file-item.model';
import { GuidGenerator } from '../shared/utilities/guid-generator.utility';
import { EdgeworthSubstrateDeltaSyncFile } from './edgeworth-data.model';
import { EdgeworthLocalFilesService } from './edgeworth-local-files.service';

// Note: This class is intentionally not marked as providedIn: 'root' so that
// it can be provided optionally through an InjectionToken, so it doesn't
// get included in non-PWA apps, where the Native Bridge is not available.
@Injectable()
export class PwaEdgeworthLocalFilesService implements EdgeworthLocalFilesService {
    private readonly pathToIdMap = new Map<string, string>();

    constructor(
        private readonly pwaNativeBridgeService: PwaNativeBridgeService
    ) {
    }

    public getLocalMru(): Observable<ReadonlyArray<EdgeworthSubstrateDeltaSyncFile>> {
        return this.pwaNativeBridgeService.getLocalMru().pipe(
            map(mruData =>
                mruData.items.map(mruDataItem => {
                    const fileLocation = mruDataItem.PathArray[0];
                    const filePath = fileLocation + '\\' + mruDataItem.FileName;

                    // The PWA Native Bridge does not provide a unique ID for a local file.
                    // Since the local files are on the device, we can use the the file path itself as a unique identifier.
                    // In order to ensure we do NOT to log this property anywhere, we map it to a generated GUID.

                    let fileId: string;

                    if (this.pathToIdMap.has(filePath)) {
                        fileId = this.pathToIdMap.get(filePath);
                    } else {
                        fileId = `LocalBridgeFile:${GuidGenerator.createGuid()}`;
                        this.pathToIdMap.set(filePath, fileId);
                    }

                    return {
                        author: null,
                        created_datetime: mruDataItem.LastAccessedTime,
                        creation_info: {
                            timestamp: mruDataItem.LastAccessedTime
                        },
                        data_source: [ 'LocalBridgeFile' ],
                        extension: FileItem.getFileExtension(mruDataItem.FileName),
                        id: fileId,
                        indexed: true,
                        is_pinned: false,
                        is_hidden: false,
                        last_recommended_modified_datetime: mruDataItem.LastAccessedTime.toISOString(),
                        last_store_modified_datetime: mruDataItem.LastAccessedTime.toISOString(),
                        mru_id: fileId,
                        mru_type: mruDataItem.FileType,
                        mru_url: mruDataItem.Url,
                        onedrive_info: null,
                        sharepoint_info: null,
                        source: 'LocalBridgeFile',
                        title: FileItem.fileNameWithoutExtension(mruDataItem.FileName),
                        type: mruDataItem.FileType,
                        subject: '',
                        url: mruDataItem.Url,
                        web_url: filePath,
                        user_relationship: {
                            last_action_datetime: mruDataItem.LastAccessedTime
                        },
                        container_title: fileLocation,
                        wset_id: fileId
                    };
                }))
        );
    }
}
